//@flow

import styled from "styled-components"
import TitleContainer from "../Shared/TitleContainer"
import Panel from "../Shared/Panel"

import { foregroundForLevel } from "../Shared/AQILegend"

import type { Level } from "../Shared/AQILegend"

export const Intro = TitleContainer.extend`
  width: 718px;
  top: 144px;
  left: 24px;
  position: absolute;
`

export const ForecastPanel = Panel.extend`
  width: 1000px;
  position: absolute;
  right: 100px;
  top: 144px;
  min-height: 680px;
  box-shadow: 0 -2px 3px rgba(0, 0, 0, 0.2);
  display: flex;
  flex-direction: row;
  padding: 2rem;
`

export const InfoPanel = Panel.extend`
  background: rgba(0, 0, 0, 0.3);
  width: 375px;
  display: flex;
  flex-direction: column;
`

export const LocalForecast = styled.div`
  margin-right: 2rem;
  flex-direction: column;
  display: flex;
  flex-grow: 1;
`

export const LocalForecastMetrics = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: row;
  flex-grow: 1;
`

export const ForecastMetricContainer = styled.li`
  flex-grow: 1;
  flex-basis: 33%;
  list-style: none;
  display: flex;
  flex-direction: column;
`

export const ForecastMetricContainerAlternate = styled.div`
  display: flex;
  flex-align: center;
  flex-direction: row;
  flex-grow: 0;
  flex-basis: 276px;
  width: 276px;
`

export const ForecastMetricTitle = styled.h5`
  font-family: "din-condensed";
  font-size: 40px;
  font-weight: 400;
  color: #fff;
  text-align: center;
  margin-bottom: 0.25rem;
  margin-top: 0;
`

export const ForecastMetricSubtitle = ForecastMetricTitle.extend`
  color: #87cefa;
  font-weight: 300;
  font-size: 24px;
  margin-top: 0;
  margin-bottom: 1rem;
`

export const ForecastMetricStatus = styled.div`
  background: rgba(0, 0, 0, 0.2);
  border: 2px solid rgba(255, 255, 255, 0.2);
  border-radius: 0.25rem;
  padding: 0.125rem 1rem;
  margin: 0.5rem 1.875rem;
`

export const ForecastMetricStatusTitle = styled.div`
  font-family: "din-condensed";
  font-size: 22px;
  color: #fff;
  text-transform: uppercase;
  text-align: center;
  font-weight: 400;
  margin: 0;
`

const colorFor = (level: Level, alpha: ?number): string => {
  const workingAlpha = typeof alpha === "number" ? alpha : 1
  switch (level) {
    case "Low":
      return `rgba(112, 190, 68, ${workingAlpha})`
    case "Medium":
      return `rgba(254, 190, 16, ${workingAlpha})`
    case "High":
      return `rgba(239, 49, 49, ${workingAlpha})`
    default:
      return `rgba(145, 145, 145, ${workingAlpha})`
  }
}

export const ForecastMetricStatusTrendContainer = styled.div`
  position: relative;
`

export const ForecastMetricStatusTrend = ForecastMetricStatusTitle.extend`
  font-size: 14px;
  margin-top: -0.25rem;
  color: ${({ level }) => colorFor(level)};
  padding-left: ${({ direction }) =>
    direction && direction !== "stable" ? 0.5 : 0}rem;

  &:before {
    content: "";
    display: block;
    width: 0;
    height: 0;
    line-height: 2rem;
    border-top: ${({ direction }) => (direction === "decreasing" ? 5 : 0)}px
      solid ${({ level }) => colorFor(level)};
    border-right: 5px solid transparent;
    border-bottom: ${({ direction }) => (direction === "increasing" ? 5 : 0)}px
      solid ${({ level }) => colorFor(level)};
    border-left: 5px solid transparent;
    position: absolute;
    top: 50%;
    margin-top: -3px;
    left: 0;
  }
`

export const ForecastProgressBarContainer = styled.div`
  margin: 2rem auto 1rem auto;
  flex-grow: 1;
  width: 80px;
  position: relative;
  display: flex;
`

export const ForecastProgressBar = styled.ul`
  list-style: none;
  letter-spacing: -0.1em;
  margin: 0;
  padding: 0;
  width: 80px;
  min-height: 200px;
  display: flex;
  flex-direction: ${({ direction }) =>
    direction === "decreasing" ? "column-reverse" : "column"};
  color: ${({ level }) => foregroundForLevel(level)};
  background: ${({ level }) => colorFor(level)};
  padding-top: ${({ direction }) => (direction === "stable" ? "20px" : "0")};
  margin-top: ${({ direction }) => (direction === "increasing" ? "20px" : "0")};
  margin-bottom: ${({ direction }) =>
    direction === "decreasing" ? "20px" : "0"};

  &:after {
    content: "";
    display: block;
    width: 0;
    height: 0;
    line-height: 2rem;
    border-top: ${({ direction }) => (direction === "decreasing" ? 40 : 0)}px
      solid ${({ level }) => colorFor(level)};
    border-right: 40px solid transparent;
    border-bottom: ${({ direction }) => (direction === "increasing" ? 40 : 0)}px
      solid ${({ level }) => colorFor(level)};
    border-left: 40px solid transparent;
    transform: scale(1, 0.5);
    transform-origin: 0
      ${({ direction }) => (direction === "increasing" ? "100%" : 0)};
    position: absolute;
    top: ${({ direction }) => (direction === "increasing" ? "-20px" : "auto")};
    bottom: ${({ direction }) =>
      direction === "decreasing" ? "-20px" : "auto"};
    left: 0;
  }

  &:before {
    content: "";
    display: block;
    width: 100%;
    height: 100%;
    position: absolute;
    top: 0;
    left: 0;
    z-index: 1;
    background: linear-gradient(
      0,
      ${({ level, direction }) =>
        colorFor(level, direction === "decreasing" ? 0 : 0.8)},
      ${({ level, direction }) =>
        colorFor(level, direction === "decreasing" ? 0.8 : 0)}
    );
  }

  & > li:first-child,
  & > li:last-child {
    margin-bottom: ${({ direction }) =>
      direction === "decreasing" ? "0" : "auto"};
    margin-top: ${({ direction }) =>
      direction === "decreasing" ? "auto" : "0"};
  }
`

export const CurrentProgressItem = styled.li`
  list-style: none;
  font-family: "din-condensed";
  font-weight: 700;
  font-size: 40px;
  line-height: 0.875em;
  text-align: center;
`

export const ProgressItemLabel = styled.span`
  display: block;
  font-size: 27px;
  line-height: 1em;
`

export const PastProgressItem = CurrentProgressItem.extend`
  font-size: 26px;
  margin: 0.35rem auto;
`

export const LocalHeadline = styled.h3`
  font-family: "din-condensed";
  font-size: 38px;
  color: #fff;
  text-align: center;
  font-weight: 400;
  margin: 0.5rem 0;
`

export const LocalSubHeadline = styled.h4`
  font-family: "din-condensed";
  font-size: 30px;
  color: #87cefa;
  text-align: center;
  font-weight: 300;
  margin: 0.5rem 0;
`

export const LocationName = styled.span`display: block;`

export const CountyHeadline = styled.h3`
  font-family: "open-sans-condensed";
  font-weight: 700;
  font-size: 24px;
  color: #fff;
  text-align: center;
`

export const CountyForecast = styled.div`
  flex-grow: 1;
  display: flex;
  align-items: center;
  flex-direction: column;
`

export const AlertStatus = styled.div`
  display: flex;
  height: 50px;
  background: ${({ healthWatch, advisory }) =>
    healthWatch ? "#ffae0e" : advisory ? "#8e1d03" : "#6fbd44"};
  color: ${({ healthWatch, advisory }) =>
    healthWatch ? "#8e1d03" : advisory ? "#ffae0e" : "#fff"};
  margin-bottom: 2rem;
`

export const AlertText = styled.div`
  font-size: 22px;
  text-transform: uppercase;
  font-family: "open-sans-condensed";
  font-weight: 700;
  margin: auto;
  margin-left: ${({ centered }) => (centered ? "auto" : "1rem")};
  text-align: center;
`

export const AlertIcon = styled.div`
  display: flex;
  align-items: center;
  margin: auto;
  margin-right: 0;
  height: 40px;
  width: 40px;
`

export const CountyDescription = styled.h5`
  color: #87cefa;
  font-family: "din-condensed";
  font-weight: 300;
  font-size: 26px;
  text-align: center;
`

export const Flag = styled.div`
  margin: auto;
  width: 226px;
  height: 138px;
`
export const Restrictions = styled.ol`
  list-style: none;
  margin: 1rem 0 0 0;
  border-top: 1px solid #669edd;
  padding-top: 1rem;
  display: flex;
  flex-direction: row;
  padding: 1rem 0 0 0;
`

export const RestrictionContainer = styled.li`
  list-style: none;
  margin: auto;
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  flex-basis: 33%;
`

export const RestrictionIcon = styled.div`
  border-radius: 45px;
  width: 90px;
  height: 90px;
  padding: 1rem;
  display: flex;
  align-items: center;
  margin: auto;
  background: ${({ allowed }) => (allowed ? "#6fbd44" : "#ff2300")};
  position: relative;
`

export const Slash = styled.span`
  border-top: 3px solid #ff2300;
  border-bottom: 3px solid #ff2300;
  width: 90px;
  left: 50%;
  margin-left: -42px;
  transform: rotate(-45deg);
  position: absolute;
  top: 50%;
`

export const RestrictionLabel = styled.h6`
  opacity: 0.7;
  color: #fff;
  font-family: "din-condensed";
  font-weight: 400;
  text-transform: uppercase;
  font-size: 18px;
  text-align: center;
  margin: auto;
  width: 100%;
  padding: 1rem 0.5rem;
`

export const ButtonGroup = styled.div`
  display: flex;
  align-items: center;
  margin-top: 1rem;

  & > * {
    flex-grow: 1;
    flex-basis: 50%;
  }
`



// WEBPACK FOOTER //
// ./src/Kiosk/CurrentAirQualityView/UI.js